import { useState, type FormEvent } from "react";
import { downloadSteps } from "@/mocks/home";
import Reveal from "./Reveal";

const SUBMIT_URL = "https://readdy.ai/api/form/daftvd3ep053ijub1vf0";

type Status = "idle" | "success" | "error";

export default function DownloadCta() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get("contact_alt")?.toString() || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    fd.delete("contact_alt");

    setLoading(true);
    setStatus("idle");
    setFormError("");
    try {
      const params = new URLSearchParams();
      fd.forEach((value, key) => params.append(key, value.toString()));

      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta || {}) as Record<string, unknown>;
      const serverMsg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        responseText;

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        const failed = /spam/i.test(serverMsg || "") ? serverMsg : serverMsg || "提交失败，请稍后重试。";
        setStatus("error");
        setFormError(failed);
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="download"
      className="section-anchor bg-background-50 py-14 md:py-20"
    >
      <div className="container-gua">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14">
          <Reveal>
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
                <i className="ri-download-2-line"></i>
                打包下载
              </span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                选好内容，一键整理打包带走
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                挑中的合集无需一篇篇手动保存。勾选感兴趣的主题，编辑部按主题归拢成离线资源包，方便你随时回看历史精华。
              </p>

              <div className="mt-8 space-y-4">
                {downloadSteps.map((s) => (
                  <div key={s.step} className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {s.step}
                    </span>
                    <div>
                      <h3 className="font-semibold text-foreground-900">{s.title}</h3>
                      <p className="mt-1 text-sm text-foreground-600">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-8 inline-flex items-center gap-2 rounded-lg bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                <i className="ri-information-line text-lg"></i>
                打包资源包按主题划分，多数合集可在线免费浏览，下载权益详见下方说明。
              </p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-lg border border-accent-300/60 bg-accent-50 p-6 md:p-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                <i className="ri-mail-send-line"></i>
                订阅领取
              </span>
              <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                留个邮箱，最新合集第一时间送达
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                订阅后，合集上新、时间线更新与打包资源包说明都会第一时间通知你，不错过任何关键细节。
              </p>

              <form
                id="gua-newsletter"
                data-readdy-form="gua-newsletter-subscribe"
                className="mt-6 space-y-4"
                onSubmit={handleSubmit}
                noValidate={false}
              >
                <div className="space-y-2">
                  <label htmlFor="nickname" className="text-sm font-medium text-foreground-800">
                    怎么称呼你（选填）
                  </label>
                  <input
                    id="nickname"
                    name="name"
                    type="text"
                    maxLength={30}
                    autoComplete="nickname"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="例如：追剧小周"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="newsletter-email" className="text-sm font-medium text-foreground-800">
                    邮箱 *
                  </label>
                  <input
                    id="newsletter-email"
                    name="email"
                    type="email"
                    required
                    maxLength={80}
                    autoComplete="email"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    placeholder="you@example.com"
                  />
                </div>

                <div className="gua-hp" aria-hidden="true">
                  <label>
                    联系方式
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                    />
                  </label>
                </div>

                {status === "success" && (
                  <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-800">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    订阅成功，合集更新将第一时间送达你的邮箱。
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line text-lg"></i>
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? (
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                  ) : (
                    <i className="ri-download-2-line text-lg"></i>
                  )}
                  {loading ? "正在提交…" : "订阅并领取资源包"}
                </button>
                <p className="text-center text-xs text-foreground-500">
                  我们只用于发送合集更新，不打扰、不推销。
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}